import React from 'react';
import ColumnDragHandle from '../components/ColumnDragHandle';
import ColumnResizeHandle from '../components/ColumnResizeHandle';

export function applyResizeToGrid(
  gridData: any[] | null | undefined,
  widths: { column: string; width: number }[],
  defaultWidths: { column: string; width: number }[] | undefined,
  colTypes: string[] | undefined,
  onColumnResize: ((column: string, width: number) => void) | undefined,
  rowHeights?: { height: number; rowNumber: number }[],
  resizeHandlesInColumnLegend = false
): any[] | null | undefined {
  if (!gridData) {
    return gridData;
  }

  const widthMap =
    widths.length > 0 ? new Map(widths.map(({ column, width }) => [column, width])) : new Map<string, number>();
  const defaultWidthMap = defaultWidths?.length
    ? new Map(defaultWidths.map(({ column, width }) => [column, width]))
    : new Map<string, number>();
  const rowHeightMap = rowHeights?.length
    ? new Map(rowHeights.map(({ rowNumber, height }) => [rowNumber, height]))
    : new Map<number, number>();

  return gridData.map((row, rowIndex) => {
    const isColumnLegendRow = rowIndex === 0 && row.some((cell: any) => cell.isLegend);
    const isFirstDataRow = rowIndex === 1;
    const shouldApplyColumnWidth = resizeHandlesInColumnLegend || rowIndex > 0;
    const shouldAttachResizeHandle = resizeHandlesInColumnLegend ? isColumnLegendRow : isFirstDataRow;
    const rowNumber = row.find((cell: any) => typeof cell.rowNumber === 'number' && cell.rowNumber > 0)?.rowNumber;

    return row.map((cell: any, colIndex: number) => {
      const key = cell.columnRef || colTypes?.[colIndex];
      const previousCell = row[colIndex - 1];
      const previousKey = previousCell?.columnRef || colTypes?.[colIndex - 1];
      const previousColLegend = previousCell?.colLegend ?? previousCell?.columnLegend ?? '';
      const w = key ? widthMap.get(key) : undefined;
      const h = rowNumber ? rowHeightMap.get(rowNumber) : undefined;
      const needsWidth = w !== undefined && shouldApplyColumnWidth;
      const needsHeight = h !== undefined;
      const needsResize = shouldAttachResizeHandle && onColumnResize && key;
      const needsLeadingResize =
        resizeHandlesInColumnLegend && shouldAttachResizeHandle && onColumnResize && previousKey && previousColLegend;

      if (!needsWidth && !needsHeight && !needsResize && !needsLeadingResize) {
        return cell;
      }

      // Sizing is kept on its own so a renderer re-reading the live cell's style can
      // stack it back on top; this copy's merged style froze when the row was built.
      const layoutStyle = {
        ...(needsWidth ? { width: w, minWidth: w, maxWidth: w } : {}),
        ...(needsHeight ? { height: h, minHeight: h, maxHeight: h } : {}),
        ...(needsResize || needsLeadingResize ? { overflow: 'visible' as const, position: 'relative' as const } : {}),
      };

      return {
        ...cell,
        layoutStyle,
        style: {
          ...(cell.style ?? {}),
          ...layoutStyle,
        },
        ...(needsResize || needsLeadingResize
          ? {
            resizeHandle: (
              <>
                {needsLeadingResize && (
                  <ColumnResizeHandle
                    columnType={previousKey}
                    colLegend={previousColLegend}
                    defaultWidth={resizeHandlesInColumnLegend ? defaultWidthMap.get(previousKey) : undefined}
                    edge="left"
                    onResize={onColumnResize}
                    variant={resizeHandlesInColumnLegend ? 'legend' : 'default'}
                  />
                )}
                {needsResize && (
                  <ColumnResizeHandle
                    columnType={key}
                    colLegend={cell.colLegend ?? cell.columnLegend ?? ''}
                    defaultWidth={resizeHandlesInColumnLegend ? defaultWidthMap.get(key) : undefined}
                    onResize={onColumnResize}
                    variant={resizeHandlesInColumnLegend ? 'legend' : 'default'}
                  />
                )}
                {cell.resizeHandle}
              </>
            ),
          }
          : {}),
      };
    });
  });
}

export function applyColumnReorderToGrid(
  gridData: any[] | null | undefined,
  colTypes: string[] | undefined,
  onColumnReorder: (sourceColumn: string, targetColumn: string) => void
): any[] | null | undefined {
  if (!gridData) {
    return gridData;
  }

  return gridData.map((row, rowIndex) => {
    // Row 0 is the column legend row. Reorder still anchors to the first data/header row.
    if (rowIndex !== 1) {
      return row;
    }

    return row.map((cell: any, colIndex: number) => {
      const key = cell.columnRef || colTypes?.[colIndex];
      if (!key) {
        return cell;
      }

      return {
        ...cell,
        style: {
          ...(cell.style ?? {}),
          position: 'relative' as const,
        },
        dragHandle: <ColumnDragHandle columnType={key} />,
      };
    });
  });
}

export function extractCellLabel(cell: any): string | null {
  const val = cell?.value;
  if (val === null || val === undefined) return null;
  if (typeof val === 'string') return val || null;
  if (typeof val === 'number') return String(val);
  // React element — try props.title (SortIndicator) or props.children
  if (typeof val === 'object' && 'props' in val) {
    if (typeof val.props?.title === 'string' && val.props.title) return val.props.title;
    if (typeof val.props?.children === 'string') return val.props.children;
  }
  return null;
}

export function columnTypeToLabel(columnType: string): string {
  return columnType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
